// docs/PERFORMANCE.md: budgets are MEASURED and shown, not asserted. Cold start → first
// meaningful paint < 400 ms, channel switch → messages painted < 100 ms, local search < 50 ms.
// Every mark is logged; the HUD (?hud=1, or localStorage satori.hud=1) shows them live.

export type Budget = 'coldStart' | 'channelSwitch' | 'localSearch';

const BUDGETS: Record<Budget, number> = { coldStart: 400, channelSwitch: 100, localSearch: 50 };

export interface PerfEntry {
  budget: Budget;
  ms: number;
  detail: string;
  over: boolean;
}

const listeners = new Set<(e: PerfEntry) => void>();
const last = new Map<Budget, PerfEntry>();
let switchStartedAt = 0;
let switchChannel = '';
let coldReported = false;

export function record(budget: Budget, ms: number, detail = ''): void {
  const entry = { budget, ms: Math.round(ms), detail, over: ms > BUDGETS[budget] };
  last.set(budget, entry);
  console.info(`[satori-perf] ${budget} ${entry.ms}ms${entry.over ? ' OVER BUDGET ' + BUDGETS[budget] : ''} ${detail}`);
  for (const l of listeners) l(entry);
}

/** Cold start, from navigation start (the page's own clock), at the first frame with content. */
export function markFirstMeaningfulPaint(detail: string): void {
  if (coldReported) return;
  coldReported = true;
  requestAnimationFrame(() => record('coldStart', performance.now(), detail));
}

/** The CLICK starts the clock (not the conversation's mount — it would restart on the wrong channel). */
export function beginChannelSwitch(channelId: string): void {
  switchStartedAt = performance.now();
  switchChannel = channelId;
}

/** Called when a conversation's first page is committed; the next frame is "painted". */
export function endChannelSwitch(channelId: string, rows: number): void {
  if (!switchStartedAt || channelId !== switchChannel) return;
  const started = switchStartedAt;
  switchStartedAt = 0;
  requestAnimationFrame(() => record('channelSwitch', performance.now() - started, `${rows} rows`));
}

export function onPerf(listener: (e: PerfEntry) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function lastPerf(): PerfEntry[] {
  return [...last.values()];
}

export function hudEnabled(): boolean {
  try {
    return new URLSearchParams(location.search).has('hud') || localStorage.getItem('satori.hud') === '1';
  } catch {
    return false;
  }
}

// MARK: - Event counters (measurement hook for "what re-reads / re-renders on a routine event")
// Plain integer bumps on a global map, read by scripts/capture.mjs (`--steps perf-idle`): worker
// change broadcasts per table, store re-reads, committed page/sidebar values, message-row renders.
// Cheap enough to stay on in production; never shown to the reader.
type Counters = Record<string, number>;
const counters: Counters = ((globalThis as { __satoriCounters?: Counters }).__satoriCounters ??= {});

export function count(name: string, by = 1): void {
  counters[name] = (counters[name] ?? 0) + by;
}
